/**
 * Settings → Security → Acting-as log (ADR-006 §6; operator decision 16: managers only, `audit.read`).
 * GET /api/v1/security/act-as-log — newest first, filtered by who, as whom and a date range, paged with
 * `next_cursor` ("Show more"). Every session: who, as whom, mode, reason, started, and how long / how it ended.
 *
 * Members never see who acted as them; this screen exists only behind `audit.read`.
 */
import { useMemo, useState } from 'react';
import { ROLE_LABEL, type ActAsLogEntry, type User } from '@amplispot/contracts';
import { useActAsLog, type ActAsLogFilters } from '../../lib/actas';
import { useUsers } from '../../lib/data';
import { useSession } from '../../lib/session';
import { Avatar, Empty, ErrorState, Field, Loading } from '../../ui/kit';

const IST = 'Asia/Kolkata';
const fmtStart = new Intl.DateTimeFormat('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const fmtTime = new Intl.DateTimeFormat('en-IN', { timeZone: IST, hour: 'numeric', minute: '2-digit' });

/** A business date (YYYY-MM-DD, India) → the instant it starts there; `to` is exclusive, so it takes the next day. */
const dayStart = (d: string) => `${d}T00:00:00+05:30`;
function nextDayStart(d: string): string {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 1);
  return dayStart(t.toISOString().slice(0, 10));
}

function duration(a: string, b: string): string {
  const min = Math.round((Date.parse(b) - Date.parse(a)) / 60_000);
  return min < 1 ? 'Under a minute' : `${min} min`;
}

const END_LABEL: Record<NonNullable<ActAsLogEntry['end_reason']>, string> = {
  stopped: 'Stopped',
  expired: 'Expired',
  replaced: 'Replaced',
  access_changed: 'Role or access changed',
  signed_out: 'Signed out',
};

export function ActAsLog() {
  const { can } = useSession();
  const users = useUsers(can('users.read'));
  const [who, setWho] = useState('');
  const [asWhom, setAsWhom] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const rangeBad = !!from && !!to && to < from;

  const filters = useMemo<ActAsLogFilters>(
    () => ({
      real_user_id: who || undefined,
      target_id: asWhom || undefined,
      from: from ? dayStart(from) : undefined,
      to: to && !rangeBad ? nextDayStart(to) : undefined,
    }),
    [who, asWhom, from, to, rangeBad],
  );
  const log = useActAsLog(filters, can('audit.read'));
  const rows = log.data?.pages.flatMap((p) => p.data) ?? [];
  const filtered = !!(who || asWhom || from || to);

  const people = useMemo(() => [...(users.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [users.data]);
  const option = (u: User) => (
    <option key={u.id} value={u.id}>
      {u.name} · {ROLE_LABEL[u.role]}
      {u.status === 'disabled' ? ' (disabled)' : ''}
    </option>
  );

  return (
    <>
      <div className="aslog-filters">
        <Field label="Who">
          <select className="in" value={who} onChange={(e) => setWho(e.target.value)} disabled={users.isPending}>
            <option value="">Anyone</option>
            {people.map(option)}
          </select>
        </Field>
        <Field label="As whom">
          <select className="in" value={asWhom} onChange={(e) => setAsWhom(e.target.value)} disabled={users.isPending}>
            <option value="">Anyone</option>
            {people.map(option)}
          </select>
        </Field>
        <Field label="Started from">
          <input className="in" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Started to" error={rangeBad ? 'Pick a date on or after the start' : undefined}>
          <input className="in" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <button
          className="btn"
          disabled={!filtered}
          onClick={() => {
            setWho('');
            setAsWhom('');
            setFrom('');
            setTo('');
          }}
        >
          Clear
        </button>
      </div>
      <div className="panel aslog">
        <div className="hd">
          <h3>Acting-as log</h3>
          <div className="r">Every session: who, as whom, why and for how long. The person acted as is not told.</div>
        </div>
        {log.isPending ? (
          <Loading label="Loading the acting-as log…" />
        ) : log.isError ? (
          <ErrorState error={log.error} onRetry={() => log.refetch()} />
        ) : rows.length === 0 ? (
          <Empty title={filtered ? 'No session matches these filters' : 'No one has acted as anyone yet'}>
            {filtered ? 'Widen the dates or pick someone else.' : 'Sessions appear here the moment someone starts acting or viewing as another person.'}
          </Empty>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Who</th>
                  <th>As whom</th>
                  <th>Mode</th>
                  <th>Reason</th>
                  <th>Started</th>
                  <th>Duration and end</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <LogRow key={r.id} r={r} />
                ))}
              </tbody>
            </table>
            <div className="more">
              {log.hasNextPage ? (
                <button className="btn sm" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage} aria-busy={log.isFetchingNextPage}>
                  {log.isFetchingNextPage ? 'Loading…' : 'Show more'}
                </button>
              ) : (
                <span className="sub">
                  {rows.length} session{rows.length === 1 ? '' : 's'}, newest first. That is all of them.
                </span>
              )}
            </div>
            {log.isFetchNextPageError ? <div className="note bad" style={{ margin: 12 }}>The next page did not load. Press Show more to try again.</div> : null}
          </>
        )}
      </div>
    </>
  );
}

function Person({ u }: { u: ActAsLogEntry['real_user'] }) {
  return (
    <div className="split">
      <Avatar user={u} size={26} />
      <div>
        <b>{u.name}</b>
        <div className="sub">{ROLE_LABEL[u.role]}</div>
      </div>
    </div>
  );
}

function LogRow({ r }: { r: ActAsLogEntry }) {
  const running = r.ended_at === null;
  return (
    <tr>
      <td>
        <Person u={r.real_user} />
      </td>
      <td>
        <Person u={r.target} />
      </td>
      <td>{r.mode === 'view' ? <span className="chip info">Viewing (read-only)</span> : <span className="chip warm">Acting</span>}</td>
      <td>
        <div className="why">{r.reason}</div>
      </td>
      <td className="num">{fmtStart.format(new Date(r.started_at))}</td>
      <td>
        {running ? (
          <span className="chip warm">Running · until {fmtTime.format(new Date(r.expires_at))}</span>
        ) : (
          <>
            <b className="num">{duration(r.started_at, r.ended_at!)}</b>
            <div className="sub">
              {r.end_reason ? END_LABEL[r.end_reason] : 'Ended'} at {fmtTime.format(new Date(r.ended_at!))}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}
